import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosInstance from "@/lib/axios";
import {
  gmailInboxAttachmentEndpoint,
  gmailInboxEndpoint,
  gmailInboxMessageEndpoint,
} from "@/config/endpoints";
import type {
  InboxAttachment,
  InboxErrorCode,
  InboxMessage,
  InboxPage,
} from "../types/gmail.types";

/**
 * Inbox = the user's own Gmail, read live by the server on every call
 * (nothing is stored in Xale). Pages follow Gmail's own `nextPageToken`.
 */
export const GMAIL_INBOX_QUERY_KEY = "gmail-inbox";

const inboxListKey = (q: string) => [GMAIL_INBOX_QUERY_KEY, "list", q];

function inboxListOptions(q: string) {
  return {
    queryKey: inboxListKey(q),
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      const res = await axiosInstance.get(gmailInboxEndpoint, {
        params: { pageToken: pageParam ?? undefined, q: q || undefined },
      });
      return res.data?.data as InboxPage;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last: InboxPage) => last?.nextPageToken ?? undefined,
    staleTime: 1000 * 30,
  };
}

interface ApiErrorBody {
  message?: string;
  data?: { code?: string };
}

function errorBody(error: unknown): ApiErrorBody | undefined {
  return (error as { response?: { data?: ApiErrorBody } } | null)?.response?.data;
}

/** The 409 `data.code` the server attaches when the inbox needs an action. */
export function inboxErrorCode(error: unknown): InboxErrorCode | null {
  const code = errorBody(error)?.data?.code;
  return typeof code === "string" ? (code as InboxErrorCode) : null;
}

export function inboxErrorMessage(
  error: unknown,
  fallback = "Couldn't load your inbox. Try again.",
): string {
  return errorBody(error)?.message || fallback;
}

export function useGmailInbox(q: string) {
  const query = useInfiniteQuery(inboxListOptions(q));
  const pages = query.data?.pages ?? [];

  return {
    messages: pages.flatMap((p) => p?.messages ?? []),
    unreadCount: pages[0]?.unreadCount ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
}

/**
 * Sidebar badge: reads whatever the inbox pane last loaded, never fetches on
 * its own (so sitting on Sent doesn't call Gmail).
 */
export function useInboxUnreadCount(): number | null {
  const query = useInfiniteQuery({ ...inboxListOptions(""), enabled: false });
  return query.data?.pages[0]?.unreadCount ?? null;
}

export function useGmailInboxMessage(id: string | null) {
  const query = useQuery({
    queryKey: [GMAIL_INBOX_QUERY_KEY, "detail", id],
    queryFn: async () => {
      const url = gmailInboxMessageEndpoint.replace(":id", encodeURIComponent(String(id)));
      const res = await axiosInstance.get(url);
      return res.data?.data as InboxMessage;
    },
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 5,
  });

  return {
    message: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export async function downloadInboxAttachment(
  messageId: string,
  attachment: InboxAttachment,
): Promise<void> {
  const url = gmailInboxAttachmentEndpoint
    .replace(":id", encodeURIComponent(messageId))
    .replace(":partId", encodeURIComponent(attachment.partId));
  try {
    const res = await axiosInstance.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(res.data as Blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = attachment.filename || "attachment";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 10_000);
  } catch {
    toast.error("Couldn't download the attachment. Try again.");
  }
}
